import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

// Types based on your backend response structure
export interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    network: string;
    icon?: string;
  };
  wallet?: {
    type: string;
    displayName: string;
    last4: string;
    brand: string;
    network: string;
  };
  displayName?: string; // For other digital wallets
}

export interface Membership {
  id: string;
  title: string;
  productId: string;
  membersAmount: number;
  accesses: string[];
  versionConstraint: string;
}

export interface Customer {
  id: string;
  email: string;
}

export interface UpgradeCosts {
  upgradeAmount: number; // Can be positive (charge) or negative (credit)
  currentMonthlyAmount: number;
  targetMonthlyAmount: number;
  currency: string;
  immediateCharge: boolean; // true = charge user, false = no charge/credit user
  isCredit: boolean; // true = user receives account credit, false = user pays
  absoluteAmount: number; // absolute value for display purposes
}

export interface InvoiceLine {
  amount: number;
  currency: string;
  product: string;
  description: string;
  period: {
    start: number;
    end: number;
  };
}

export interface UpgradeData {
  paymentMethod: PaymentMethod | null;
  membership: Membership;
  customer: Customer;
  targetMembership: Membership;
  costs: UpgradeCosts;
  invoiceLines: InvoiceLine[];
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const upgradeData = ref<UpgradeData | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchTime = ref<number | null>(null);

  const hasData = computed(() => !!upgradeData.value);
  const paymentMethod = computed(() => upgradeData.value?.paymentMethod || null);
  const membership = computed(() => upgradeData.value?.membership || null);
  const customer = computed(() => upgradeData.value?.customer || null);
  const targetMembership = computed(() => upgradeData.value?.targetMembership || null);
  const costs = computed(() => upgradeData.value?.costs || null);
  const invoiceLines = computed(() => upgradeData.value?.invoiceLines || []);

  // Computed values for easy access to common cost data
  const upgradeAmount = computed(() => costs.value?.upgradeAmount || 0);
  const currentMonthlyAmount = computed(() => costs.value?.currentMonthlyAmount || 0);
  const targetMonthlyAmount = computed(() => costs.value?.targetMonthlyAmount || 0);
  const currency = computed(() => costs.value?.currency || 'usd');
  const immediateCharge = computed(() => costs.value?.immediateCharge || false);
  const isCredit = computed(() => costs.value?.isCredit || false);
  const absoluteAmount = computed(() => costs.value?.absoluteAmount || Math.abs(costs.value?.upgradeAmount || 0));

  // Helper computed for invoice line items
  const creditLineItems = computed(() => invoiceLines.value.filter(line => line.amount < 0));
  const chargeLineItems = computed(() => invoiceLines.value.filter(line => line.amount > 0));

  // Helper computed to check if payment method is required for upgrades
  const needsPaymentMethod = computed(() => {
    return !paymentMethod.value && !isCredit.value;
  });

  function setUpgradeData(data: UpgradeData) {
    upgradeData.value = data;
    lastFetchTime.value = Date.now();
    error.value = null;
  }

  function setLoading(loading: boolean) {
    console.log('🔄 Store setLoading called:', loading);
    isLoading.value = loading;
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage;
  }

  function clearData() {
    upgradeData.value = null;
    error.value = null;
    lastFetchTime.value = null;
    isLoading.value = false;
  }

  return {
    // State
    upgradeData,
    isLoading,
    error,
    lastFetchTime,

    // Computed
    hasData,
    paymentMethod,
    membership,
    customer,
    targetMembership,
    costs,
    invoiceLines,
    upgradeAmount,
    currentMonthlyAmount,
    targetMonthlyAmount,
    currency,
    immediateCharge,
    isCredit,
    absoluteAmount,
    creditLineItems,
    chargeLineItems,
    needsPaymentMethod,

    // Actions
    setUpgradeData,
    setLoading,
    setError,
    clearData,
  };
});
